import React, { useState, useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import { endpoint } from '../main';

interface MusicTrack {
  id: string;
  title: string;
  artist: string;
  coverImage: string;
  audioUrl: string;
}

const PlayerContainer = styled.div`
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 1000;
  width: 50px;
  height: 50px;
  cursor: pointer;
  transition: transform 0.2s;
  &:hover {
    transform: scale(1.1);
  }
`;

const CoverImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 8px;
`;

const PlayButton = styled.div<{ isPlaying: boolean }>`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 0;
  height: 0;
  border-style: solid;
  border-width: 10px 0 10px 16px;
  border-color: transparent transparent transparent rgba(255, 255, 255, 0.8);
  opacity: ${props => props.isPlaying ? 0 : 1};
  transition: opacity 0.2s;
`;

const MusicPlayer: React.FC = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTrack, setCurrentTrack] = useState<MusicTrack | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [tracks, setTracks] = useState<MusicTrack[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [lastTrackChangeTime, setLastTrackChangeTime] = useState<number>(0);

  // Function to get a random track based on the current hour
  const getHourlyTrack = (trackList: MusicTrack[]) => {
    if (trackList.length === 0) return null;
    
    const now = Date.now();
    const currentHour = Math.floor(now / (60 * 60 * 1000));
    
    // If it's been less than an hour since the last track change, keep the same track
    if (currentTrack && (now - lastTrackChangeTime) < 60 * 60 * 1000) {
      return currentTrack;
    }
    
    // Use the current hour as a seed for randomization
    const seed = currentHour % trackList.length;
    const shuffledTracks = [...trackList].sort(() => 0.5 - Math.random());
    const selectedTrack = shuffledTracks[seed % shuffledTracks.length];
    
    setLastTrackChangeTime(now);
    return selectedTrack;
  };

  useEffect(() => {
    setIsLoading(true);
    // Fetch tracks from the backend
    fetch(`${endpoint}/music/tracks`, {
      credentials: 'include',
      headers: {
        'Accept': 'application/json'
      }
    })
      .then(res => {
        if (!res.ok) {
          throw new Error(`HTTP error! status: ${res.status}`);
        }
        return res.json();
      })
      .then(data => {
        if (Array.isArray(data)) {
          setTracks(data);
          if (data.length > 0) {
            const track = getHourlyTrack(data);
            setCurrentTrack(track);
          }
        } else {
          console.error('Invalid response format:', data);
          setError('Invalid response format');
        }
      })
      .catch(error => {
        console.error('Error fetching tracks:', error);
        setError('Failed to load music tracks');
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  // Check for hour changes
  useEffect(() => {
    const checkHourChange = () => {
      if (tracks.length > 0) {
        const track = getHourlyTrack(tracks);
        if (track && track.id !== currentTrack?.id) {
          setCurrentTrack(track);
          if (isPlaying) {
            // Wait for the audio to be ready before playing
            audioRef.current?.addEventListener('canplay', () => {
              audioRef.current?.play();
            }, { once: true });
          }
        }
      }
    };

    const interval = setInterval(checkHourChange, 60000); // Check every minute
    return () => clearInterval(interval);
  }, [tracks, currentTrack, isPlaying]);

  const togglePlay = () => {
    if (!currentTrack) return;
    
    if (isPlaying) {
      audioRef.current?.pause();
    } else {
      audioRef.current?.play();
    }
    setIsPlaying(!isPlaying);
  };

  if (isLoading) {
    return null;
  }

  if (error) {
    console.error('Music player error:', error);
    return null;
  }

  if (!currentTrack) {
    console.log('No tracks available');
    return null;
  }

  return (
    <PlayerContainer onClick={togglePlay}>
      <CoverImage src={currentTrack.coverImage} alt={currentTrack.title} />
      <PlayButton isPlaying={isPlaying} />
      <audio
        ref={audioRef}
        src={currentTrack.audioUrl}
        preload="auto"
        loop
        onError={(e) => {
          console.error('Audio error:', e);
          setError('Failed to load audio track');
        }}
      />
    </PlayerContainer>
  );
};

export default MusicPlayer; 